import React, { useState, useEffect, useMemo, useCallback } from 'react'
import messages from './messages'
import { DataTable, Button, Dropdown, Table, Chip, DropdownMenu, InputInline, Icon } from '@arnsbogroup/react-voicearchive'
import { useDropzone } from 'react-dropzone'
import classNames from 'classnames'
import NiceModal from '@ebay/nice-modal-react'
import ShareFileModal from 'components/ShareFileModal'
import BulkDeliverFilesModal from './components/BulkDeliverFilesModal'
import { useIntl } from 'react-intl'
import { formatBytes } from 'utils'

import {
  Span,
  Timestamp,
  FileVisibility,
  FileDownloadStatus,
  FileOwnerType,
  FileMeta,
  FilePreview,
} from 'components/datatable'

const Title = (props) => {
  const  { onChange, taskId, ...file } = props

  return (
    <div className="flex items-center space-x-2 group" title={`${file.title} (${formatBytes(file.filesize)})`}>
      { +taskId !== +file.task &&
        <Chip state="warning" title="Job file">Job</Chip>
      }
      <div className='truncate'>
        <InputInline
          type="text"
          value={file.title}
          onBlur={({ target: { value } }) => onChange({ ...file, title: value })}
        />
      </div>
      <Icon
        onClick={() => window.open(`/download/task/file/${file.uid}`, '_blank')}
        className='opacity-0 group-hover:opacity-100 text-xs cursor-pointer text-primary'
        icon={['fal', 'download']}
      />
    </div>
  )
}


const TaskFiles = ({ fetchFiles, files, setFiles, task, loading = false }) => {
  const { formatMessage } = useIntl()

  const [uploadingFiles, setUploadingFiles] = useState(false)
  const [selectedFileIds, setSelectedFileIds] = useState([])

  const filesEntries = useMemo(() => [
    ...files?.sort((a, b) => {
      if (a.issource === b.issource) {
        return b.uid < a.uid ? -1 : 1
      } else {
        return a.issource < b.issource ? -1 : 1
      }
    }) || [],
  ], [files])


  const onFileChange = (file) => {
    setFiles((prev) => prev.map((f) => f.uid === file.uid ? file : f))

    window.toastr.info('Saving file changes..')

    fetch(`/rest/v1/task/${task.uid}/files/${file.uid}`, {
      method: 'PUT',
      body: JSON.stringify(file),
    })
      .then((response) => response.json())
      .then((response) => window.toastr.success('File changes saved'))
      .then(fetchFiles)
      .catch((error) => window.alert(error))
  }

  const onFileDrop = useCallback((acceptedFiles) => {
    setUploadingFiles(true)

    const promises = acceptedFiles.map((file) => {
      const body = new FormData()
      body.append('taskid', task.uid)
      body.append('addFileIsSource', 1)
      body.append('addFileWordcount', 0)
      body.append('addFileFile', file)

      return fetch(`/ordertask/uploadspeak.json`, { method: 'POST', body })
        .then((response) => response.json())
        .then(({ success, errors }) => !success && window?.toastr.error(errors.join(', ')))
    })

    Promise.all(promises)
      .then(() => setUploadingFiles(false))
      .then(fetchFiles)
      .catch((error) => window?.toastr.error(error))
  }, [task])

  const { acceptedFiles, getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop: onFileDrop })

  return (
    <div>
      <DataTable
        className="table-fixed"
        rowActions={[
          {
            label: 'Download file',
              onClick: (file) => window.open(`/download/task/file/${file.uid}`, '_blank'),
          },
          {
            label: 'Copy public link',
              onClick: (file) => NiceModal.show(ShareFileModal, { file, anonymous: false }),
          },
          {
            label: 'Copy anonymous link',
            onClick: (file) => NiceModal.show(ShareFileModal, { file, anonymous: true }),
          },
          {
            label: (
              <a
                href="https://my.voicearchive.com/workgroups/group/326/tasks/task/view/66548/"
                className="flex justify-between items-center"
                target="_blank"
              >
                <span>Convert</span>
                <Chip state="warning">Depreacted</Chip>
              </a>
            ),
          },
          {
            state: 'negative',
            label: 'Delete',
            isVisible: (file) => +task.uid == +file.task,
            onClick: ({ uid }) => {
              if(confirm(formatMessage(messages.confirm))) {
                const body = new FormData()
                body.append('fileid', uid)
                fetch(`/ordertask/removefile.html`, { method: 'POST', body })
                  .then((response) => response.text())
                  .then((body) => {
                    if (body === 'ok') {
                      fetchFiles()
                      toastr.success('File deleted')
                    } else {
                      toastr.error("Failed to delete file")
                    }
                  })
                  .catch((error) => toastr.error(error))
              }
            }
          },
        ]}
        rowSelectionKey="uid"
        rowsSelected={selectedFileIds}
        onRowSelected={(key) => {
          if (selectedFileIds.includes(key)) {
            setSelectedFileIds((prev) => prev.filter((id) => id !== key))
          } else {
            setSelectedFileIds((prev) => [...prev, key])
          }
        }}
        indicators={[
          FileDownloadStatus,
          (file) => (
            <span className="cursor-pointer" onClick={() => onFileChange({ ...file, hidefile: Boolean(+file.hidefile) ? 0 : 1 })}>
              <FileVisibility {...file} />
            </span>
          ),
          (file) => (
            <span className="cursor-pointer" onClick={() => onFileChange({ ...file, issource: Boolean(+file.issource) ? 0 : 1 })}>
              <FileOwnerType {...file} />
            </span>
          ),
        ]}
        components={{
          Td: ({ childProps = {}, ...props }) => (
            <Table.Td
              className={classNames({
                'line-through opacity-50': Boolean(Number(childProps.deleted)),
                'opacity-50': Boolean(Number(childProps.archived)),
              })}
              {...props }
            />
          )
        }}
        head={{
          title: { className: 'w-4/12', label: 'Audio' },
          cdn_url: { className: 'w-2/12', label: 'Preview' },
          crdate: { className: 'w-2/12', label: 'Uploaded at' },
          duration: { className: 'w-2/12', label: 'Meta' },
        }}
        body={{
          title: (props) => <Title {...props} taskId={task.uid} onChange={onFileChange} />,
          cdn_url: FilePreview,
          crdate: (file) => <Timestamp className="truncate">{file?.crdate}</Timestamp>,
          duration: (file) => <FileMeta {...file} className="truncate" onChange={onFileChange} />,
        }}
        entries={filesEntries}
      />

      <div className="w-full h-20 flex items-center justify-center shadow-inner cursor-pointer" {...getRootProps()}>
        <div>
          <input {...getInputProps()} />

          <div className="w-full text-sm text-center px-2 py-4 text-gray-500">
            {
              uploadingFiles ?
                <p>Uploading files..</p>
                : loading ?
                  <p>Loading files..</p>
                  : isDragActive ?
                    <p>Drop the files here ...</p>
                    :
                    <p>Drag and drop files here, or <span className="text-primary font-medium">click</span> to select files</p>
            }
          </div>
        </div>
      </div>

      <div className="flex justify-start space-x-4 px-4">
        <DropdownMenu
          button={{
            size: 'sm',
            icon: ['fal', 'ellipsis-vertical'],
            children: 'Bulk actions',
            disabled: selectedFileIds.length === 0,
          }}
          items={[
            {
              children: 'Deliver as ZIP archive',
                onClick: () => NiceModal.show(BulkDeliverFilesModal, { fileIds: selectedFileIds, task: task }),
            },
            {
              children: 'Download as ZIP archive',
              onClick: () => {
                window?.toastr.info('Preparing files. Please wait..')
                const body = new FormData()
                body.append('fileIds', selectedFileIds)
                body.append('taskId', task.uid)
                fetch(`/file/downloadSelectedTaskFiles.html`, { method: 'POST', body })
                  .then((response) => response.blob())
                  .then((blob) => {
                    const file = window.URL.createObjectURL(blob)
                    window.location.assign(file)
                  })
                  .then(() => window?.toastr.success('Downloading ZIP archive..'))
                  .catch((error) => toastr.error(error))
              },
            },
            {
              children: 'Compress to ZIP archive',
              onClick: () => {
                const body = JSON.stringify({
                  fileIds: selectedFileIds,
                  taskId: task.uid
                })
                fetch(`/rest/v2/file/convert-to-zip`, { method: 'POST', body })
                  .then((response) => response.json())
                  .then(fetchFiles)
                  .catch((error) => toastr.error(error))
              },
            },
            {
              children: 'Delete',
              onClick: () => {
                const body = new FormData()
                body.append('fileIds', selectedFileIds)
                body.append('taskId', task.uid)
                fetch(`/ordertask/deleteSelected.html`, { method: 'POST', body })
                  .then(fetchFiles)
                  .catch((error) => toastr.error(error))
              },
            },
          ]}
        />
      </div>
    </div>
  )
}

export default TaskFiles
